import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { C, EMPTY, toBody, useClaimsConfig, refreshClaimSuggestions } from "@/lib/claims";
import { Section, CustomerVehicleFields, ClaimInfoFields, SurveyorFields } from "@/components/claims/ClaimForm";

// A claim is its own record — no job, quote or calendar entry is required, read or created.
export default function ClaimNew() {
  const navigate = useNavigate();
  const cfg = useClaimsConfig();
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { const { data } = await axios.post(C, toBody(form)); refreshClaimSuggestions(); toast.success(`Claim ${data.claim_number} created`); navigate(`/claims/${data.id}`); }
    catch (e) { toast.error("Could not create claim", { description: typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Check the fields" }); }
    finally { setSaving(false); }
  };
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 pb-28 md:pb-8">
      <AppHeader />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-3" data-testid="claim-new-page">
        <div className="flex items-center justify-between gap-3">
          <Link to="/claims" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-rose-300" data-testid="claim-back"><ArrowLeft className="w-3.5 h-3.5" /> Insurance Claims</Link>
          <span className="text-[10px] uppercase tracking-[0.2em] text-rose-400 font-bold">New insurance claim</span>
        </div>
        <div><h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Open a claim</h1><p className="text-slate-400 text-xs mt-1">Claim number is generated from the accident date and plate (e.g. CLM-20260928-SXX1234A). Starts at <b className="text-slate-200">Pending Surveyor Viewing</b>.</p></div>
        <Section title="1 · Customer & vehicle" testid="claim-customer"><CustomerVehicleFields form={form} setForm={setForm} /></Section>
        <Section title="2 · Claim information" testid="claim-info"><ClaimInfoFields form={form} setForm={setForm} insurers={cfg.insurers} /></Section>
        <Section title="3 · Surveyor" testid="claim-surveyor"><SurveyorFields form={form} setForm={setForm} /></Section>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] text-slate-500">Surveyor progress, costs, settlement, notes and documents are added on the claim page after creation.</p>
          <div className="flex gap-2"><Button variant="outline" onClick={() => navigate(-1)} className="h-9 border-slate-700 bg-transparent text-slate-200" data-testid="claim-cancel">Cancel</Button><Button onClick={save} disabled={saving} data-testid="claim-create" className="h-9 bg-rose-500 hover:bg-rose-400 text-slate-950 font-bold px-5">{saving ? "Creating…" : "Create claim"}</Button></div>
        </div>
      </main>
    </div>
  );
}
